import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { unwrap } from '@/api/envelope';
import { staffOrganizationActivity, type OrganizationResponse } from '@/api/generated';
import { ROUTES } from '@/app/routes';
import { ErrorState } from '@/components/ui/ErrorState';

import { ActivityPanel } from './ActivityPanel';
import { ORGANIZATION_PARAM } from './StaffActivityPage';
import { toActivityEntries, toFeedEntries } from './accountActivity';

/** How many entries the panel shows; the rest are on the Activity page. */
const PANEL_SIZE = 10;

/**
 * The account page's Recent activity: the latest entries from this account's
 * audit feed, and a way through to all of them.
 *
 * Its own query and its own failure state, as the Super Admins card has, so a
 * feed outage leaves the rest of the account readable. An account older than
 * the log has an empty feed; for that one the panel shows the dates the record
 * itself holds, as it always did, and claims nobody did them.
 */
export function RecentActivity({ record }: { record: OrganizationResponse }) {
  const feed = useQuery({
    queryKey: ['staff', 'organizations', record.id, 'activity'],
    queryFn: async () => {
      const reply = await staffOrganizationActivity({
        path: { organization_id: record.id },
        query: { page: 1, limit: PANEL_SIZE },
        throwOnError: true,
      });
      return unwrap(reply.data)?.items ?? [];
    },
  });

  if (feed.isPending) {
    // Rows the shape of the panel, as the Super Admins card does, rather than
    // a line of text: the browser tests treat a placeholder as "still
    // loading" and wait for it, so the page is measured once the feed is in.
    // The page's own placeholder announces the loading; this does not again.
    return (
      <Stack
        spacing={1}
        aria-label="Loading activity"
        data-testid="recent-activity-loading"
      >
        <Skeleton height={44} />
        <Skeleton height={44} />
        <Skeleton height={44} />
      </Stack>
    );
  }

  if (feed.isError) {
    return (
      <ErrorState
        title="This account’s activity could not be loaded."
        onRetry={() => void feed.refetch()}
        testId="recent-activity"
      />
    );
  }

  const fromFeed = feed.data.length > 0;

  return (
    <ActivityPanel
      embedded
      entries={fromFeed ? toFeedEntries(feed.data) : toActivityEntries(record)}
      footer={
        <Link
          component={RouterLink}
          to={`${ROUTES.staff.activity}?${ORGANIZATION_PARAM}=${record.id}`}
          variant="body2"
          data-testid="recent-activity-view-all-link"
        >
          View all activity
        </Link>
      }
    />
  );
}
